import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import '../styles/PostDetailBottomMenu.css';
import CommentsSection from './CommentsSection';
import EchoesLogo from './EchoesLogo';
import EchoesRating from './EchoesRating';

const PostDetailBottomMenu = forwardRef(({ 
  post, 
  commentCount = 0,
  onCommentClick,
  onStepsClick,
  onEchoClick,
  onShareClick,
  className = '',
  style = {}
}, ref) => {
  const [activePanel, setActivePanel] = useState(null);
  const [showFullscreenComments, setShowFullscreenComments] = useState(false);
  
  // État local pour le nombre de commentaires (mis à jour en temps réel)
  const [realTimeCommentCount, setRealTimeCommentCount] = useState(commentCount);
  
  // États pour la gestion tactile du panel
  const [panelDragY, setPanelDragY] = useState(0);
  const [panelIsDragging, setPanelIsDragging] = useState(false);
  const [panelStartY, setPanelStartY] = useState(0);
  
  // Effet pour empêcher le rebond de la page lorsqu'un panneau est actif
  useEffect(() => {
    // Fonction pour empêcher l'effet de rebond du scroll
    const preventBounce = (e) => {
      // Ne pas empêcher le défilement normal
      const panel = document.querySelector('.sliding-panel.active');
      if (!panel) return;
      
      // Empêcher l'effet de rebond uniquement sur iOS
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      if (isIOS && activePanel) {
        const panelContent = panel.querySelector('.panel-body');
        if (panelContent) {
          // Autoriser le défilement uniquement si on n'est pas au bout
          const isAtTop = panelContent.scrollTop <= 0;
          const isAtBottom = panelContent.scrollHeight - panelContent.scrollTop <= panelContent.clientHeight + 1;
          
          if ((isAtTop && e.deltaY < 0) || (isAtBottom && e.deltaY > 0)) {
            e.preventDefault();
          }
        }
      }
    };
    
    // Installer les écouteurs d'événements lorsqu'un panneau est actif
    if (activePanel) {
      document.addEventListener('wheel', preventBounce, { passive: false });
      document.addEventListener('touchmove', preventBounce, { passive: false });
    }
    
    return () => {
      // Nettoyer les écouteurs
      document.removeEventListener('wheel', preventBounce);
      document.removeEventListener('touchmove', preventBounce);
      
      // Réinitialiser l'overflow
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [activePanel]);
  
  // Déterminer la classe CSS pour le panneau de commentaires en fonction du nombre
  const getCommentsClass = useCallback(() => {
    if (realTimeCommentCount === 0) return 'comments-empty';
    if (realTimeCommentCount <= 2) return 'comments-few';
    if (realTimeCommentCount <= 5) return 'comments-some';
    if (realTimeCommentCount <= 10) return 'comments-many';
    return 'comments-lots';
  }, [realTimeCommentCount]);

  // Gestionnaires tactiles pour le sliding panel
  const handlePanelTouchStart = useCallback((e) => {
    // Empêcher la propagation pour éviter que le scroll de page n'interfère
    e.stopPropagation();
    
    // Détection iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    if (isIOS) {
      // Empêcher l'effet de rebond sur iOS
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    }
    
    setPanelIsDragging(true);
    setPanelStartY(e.touches[0].clientY);
    setPanelDragY(0);
  }, []);

  const handlePanelTouchMove = useCallback((e) => {
    if (!panelIsDragging) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const currentY = e.touches[0].clientY;
    // Déterminer la direction du mouvement
    const movingDown = currentY > panelStartY;
    
    // Si détection d'un rebond (mouvement très rapide vers le haut puis bas)
    const deltaTime = Date.now() - (window._lastTouchTime || 0);
    window._lastTouchTime = Date.now();
    
    // Protection contre les rebonds (mouvements rapides inversés)
    const isBounceEffect = deltaTime < 50 && Math.abs(currentY - panelStartY) > 50;
    
    // Limiter le déplacement vers le haut pour éviter des problèmes
    // Et annuler complètement le mouvement si c'est un effet de rebond
    let deltaY = 0;
    if (movingDown && !isBounceEffect) {
      deltaY = Math.max(0, currentY - panelStartY);
      setPanelDragY(deltaY);
    } else {
      setPanelDragY(0);
    }
    
    // Feedback visuel pendant le drag uniquement si on descend intentionnellement
    const panel = document.querySelector('.sliding-panel.active');
    if (panel) {
      if (movingDown && !isBounceEffect) {
        panel.style.transform = `translateY(${deltaY}px)`;
        panel.style.opacity = Math.max(0.5, 1 - deltaY / 400);
      } else {
        // Fixe en position ouverte si mouvement vers le haut ou rebond
        panel.style.transform = 'translateY(0)';
        panel.style.opacity = '1';
        panel.classList.add('no-bounce');
      }
    }
  }, [panelIsDragging, panelStartY]);

  const handlePanelTouchEnd = useCallback(() => {
    if (!panelIsDragging) return;
    
    setPanelIsDragging(false);
    const panel = document.querySelector('.sliding-panel.active');
    
    // Ajout d'une protection contre le "bounce effect"
    // N'autoriser la fermeture que si le drag est intentionnel (> 150px)
    if (panelDragY > 150) {
      // Fermer si drag > 150px (seuil plus élevé pour éviter les fermetures accidentelles)
      // Accélérer la transition de fermeture pour une meilleure réactivité
      if (panel) {
        panel.style.transition = 'all 0.2s cubic-bezier(0.25, 0.1, 0.25, 1)';
      }
      
      // Réinitialiser l'overflow pour permettre le défilement normal
      setTimeout(() => {
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
      }, 200);
      
      handleSetActivePanel(null);
    } else {
      // Remettre en place
      if (panel) {
        // Accélérer la transition de retour pour plus de réactivité
        panel.style.transition = 'all 0.15s cubic-bezier(0.25, 0.1, 0.25, 1)';
        panel.style.transform = '';
        panel.style.opacity = '';
        
        // Rétablir la transition normale après l'animation rapide
        setTimeout(() => {
          if (panel) {
            panel.style.transition = '';
          }
        }, 200);
      }
    }
    
    // Assurer que le style est toujours réinitialisé
    setTimeout(() => {
      const activePanel = document.querySelector('.sliding-panel.active');
      if (activePanel) {
        activePanel.style.transform = '';
        activePanel.style.opacity = '';
        // Remettre la transition par défaut après un certain délai
        setTimeout(() => {
          if (activePanel) {
            activePanel.style.transition = '';
          }
        }, 300);
      }
    }, 50);
    
    setPanelDragY(0);
  }, [panelIsDragging, panelDragY]);

  useEffect(() => {
    if (activePanel) {
      // Tout comportement supplémentaire nécessaire lors de l'activation d'un panneau
    }
  }, [activePanel]);

  // Synchroniser le compteur local avec le prop commentCount
  useEffect(() => {
    setRealTimeCommentCount(commentCount);
  }, [commentCount]);

  // S'assurer que l'UI se met à jour quand le compteur de commentaires change
  useEffect(() => {
    // La classe CSS s'actualisera automatiquement grâce au changement de realTimeCommentCount
  }, [realTimeCommentCount, activePanel]);

  // Listener pour les changements d'orientation et de taille d'écran
  useEffect(() => {
    const handleResize = () => {
      // Les media queries CSS gèrent automatiquement les changements de taille d'écran
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [activePanel]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Escape') {
        setShowFullscreenComments(false);
        setActivePanel(null);
      }
    };

    if (activePanel || showFullscreenComments) {
      document.addEventListener('keydown', handleKeyPress);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
      document.body.style.overflow = 'unset';
    };
  }, [activePanel, showFullscreenComments]);

  // Fonction pour gérer le changement de panneau actif
  const handleSetActivePanel = (panel) => {
    // Ajouter la classe pour empêcher le rebond sur iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    
    if (panel) {
      if (isIOS) {
        document.body.classList.add('panel-open');
        document.documentElement.classList.add('panel-open');
      }
    } else {
      // Nettoyer les classes lors de la fermeture
      document.body.classList.remove('panel-open');
      document.documentElement.classList.remove('panel-open');
    }
    
    setActivePanel(panel);
  };

  const handleMenuClick = (panel) => {
    if (panel === activePanel) {
      handleSetActivePanel(null);
    } else {
      handleSetActivePanel(panel);
      setShowFullscreenComments(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleSetActivePanel(null);
      setShowFullscreenComments(false);
    }
  };

  // Exposer les méthodes pour le parent
  useImperativeHandle(ref, () => ({
    openCommentsPanel: () => {
      handleSetActivePanel('comments');
    }
  }));

  return React.createElement('div', { 
    className: `post-detail-bottom-menu ${className}`,
    style: style
  },
  React.createElement('div', { className: 'bottom-menu' },
    React.createElement('button', {
      className: `bottom-menu-btn ${activePanel === 'comments' ? 'active' : ''}`,
      onClick: () => handleMenuClick('comments')
    },
    React.createElement('div', { className: 'btn-content' },
      React.createElement('svg', {
        width: '18',
        height: '18',
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: '#6b7280',
        strokeWidth: '2'
      },
      React.createElement('path', { 
        d: 'M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z' 
      })
      ),
      React.createElement('span', { className: 'btn-label' }, 'Commenter'),
      realTimeCommentCount > 0 && React.createElement('span', { className: 'btn-count' }, realTimeCommentCount)
    )
    ),
      
    React.createElement('button', {
      className: `bottom-menu-btn ${activePanel === 'steps' ? 'active' : ''}`,
      onClick: () => handleMenuClick('steps')
    },
    React.createElement('div', { className: 'btn-content' },
      React.createElement('svg', {
        width: '18',
        height: '18',
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: '#6b7280',
        strokeWidth: '2'
      },
      React.createElement('path', { d: 'M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2' }),
      React.createElement('rect', { x: '8', y: '2', width: '8', height: '4', rx: '1', ry: '1' }),
      React.createElement('path', { d: 'M9 12l2 2 4-4' })
      ),
      React.createElement('span', { className: 'btn-label' }, 'Étapes'),
      post?.methodSteps?.length > 0 && React.createElement('span', { className: 'btn-count' }, post.methodSteps.length)
    )
    ),

    React.createElement('button', {
      className: `bottom-menu-btn ${activePanel === 'echoes' ? 'active' : ''}`,
      onClick: () => handleMenuClick('echoes')
    },
    React.createElement('div', { className: 'btn-content' },
      React.createElement(EchoesLogo, {
        size: 22,
        color: '#6b7280'
      }),
      React.createElement('span', { className: 'btn-label' }, 'Noter')
    )
    ),

    React.createElement('button', {
      className: `bottom-menu-btn ${activePanel === 'share' ? 'active' : ''}`,
      onClick: () => handleMenuClick('share')
    },
    React.createElement('div', { className: 'btn-content' },
      React.createElement('svg', {
        width: '18',
        height: '18',
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: '#6b7280',
        strokeWidth: '2'
      },
      React.createElement('circle', { cx: '18', cy: '5', r: '3' }),
      React.createElement('circle', { cx: '6', cy: '12', r: '3' }),
      React.createElement('circle', { cx: '18', cy: '19', r: '3' }),
      React.createElement('line', { x1: '8.59', y1: '13.51', x2: '15.42', y2: '17.49' }),
      React.createElement('line', { x1: '15.41', y1: '6.51', x2: '8.59', y2: '10.49' })
      ),
      React.createElement('span', { className: 'btn-label' }, 'Partager')
    )
    )
  ),

  // Overlay for sliding panels
  activePanel && React.createElement('div', {
    className: `sliding-panel-backdrop ${activePanel ? 'active' : ''}`,
    onClick: handleOverlayClick
  }),

  // Comments Sliding Panel
  activePanel === 'comments' && React.createElement('div', {
    className: `sliding-panel comments-panel ${getCommentsClass()} ${activePanel === 'comments' ? 'active' : ''}`
  },
  React.createElement('div', { className: 'panel-content' },
    // Header zone avec gestion tactile pour le glissement
    React.createElement('div', { 
      className: 'panel-header-zone',
      onTouchStart: handlePanelTouchStart,
      onTouchMove: handlePanelTouchMove,
      onTouchEnd: handlePanelTouchEnd
    },
    // Handle de drag
    React.createElement('div', { className: 'panel-drag-handle' },
      React.createElement('div', { className: 'drag-indicator' })
    ),
    React.createElement('div', { className: 'panel-header' },
      React.createElement('h3', null, 
        'Commentaires ',
        React.createElement('span', { className: 'comment-count' }, `(${realTimeCommentCount})`)
      ),
      React.createElement('button', {
        className: 'panel-close-btn',
        onClick: () => handleSetActivePanel(null)
      }, '✕')
    )
    ),
    React.createElement('div', { className: 'panel-body' },
      React.createElement(CommentsSection, {
        postId: post?.id,
        postAuthor: post?.author || post?.userId,
        onCommentCountChange: (newCount) => {
          setRealTimeCommentCount(newCount);
        },
      })
    )
  )
  ),

  // Echoes Rating Sliding Panel
  activePanel === 'echoes' && React.createElement('div', {
    className: `sliding-panel echoes-panel ${activePanel === 'echoes' ? 'active' : ''}`
  },
  React.createElement('div', { className: 'panel-content' },
    // Header zone avec gestion tactile pour le glissement
    React.createElement('div', { 
      className: 'panel-header-zone',
      onTouchStart: handlePanelTouchStart,
      onTouchMove: handlePanelTouchMove,
      onTouchEnd: handlePanelTouchEnd
    },
    React.createElement('div', { className: 'panel-drag-handle' },
      React.createElement('div', { className: 'drag-indicator' })
    ),
    React.createElement('div', { className: 'panel-header' },
      React.createElement('h3', null, 'Notation ECHOES'),
      React.createElement('button', {
        className: 'panel-close-btn',
        onClick: () => handleSetActivePanel(null)
      }, '✕')
    )
    ),
    React.createElement('div', { className: 'panel-body' },
      React.createElement(EchoesRating, {
        postId: post?.id,
        postAuthorId: post?.author || post?.userId,
        onRatingChange: (postId, ratings, isSuccess) => {
          // Fermer automatiquement le panel si la publication a réussi
          if (isSuccess) {
            handleSetActivePanel(null);
          }
        },
      })
    )
  )
  ),

  // Share Sliding Panel
  activePanel === 'share' && React.createElement('div', {
    className: `sliding-panel share-panel ${activePanel === 'share' ? 'active' : ''}`
  },
  React.createElement('div', { className: 'panel-content' },
    // Header zone avec gestion tactile pour le glissement
    React.createElement('div', { 
      className: 'panel-header-zone',
      onTouchStart: handlePanelTouchStart,
      onTouchMove: handlePanelTouchMove,
      onTouchEnd: handlePanelTouchEnd
    },
    React.createElement('div', { className: 'panel-drag-handle' },
      React.createElement('div', { className: 'drag-indicator' })
    ),
    React.createElement('div', { className: 'panel-header' },
      React.createElement('h3', null, 'Partager'),
      React.createElement('button', {
        className: 'panel-close-btn',
        onClick: () => handleSetActivePanel(null)
      }, '✕')
    )
    ),
    React.createElement('div', { className: 'panel-body' },
      React.createElement('div', { className: 'share-options' },
        React.createElement('p', null, 'Options de partage à venir...')
      )
    )
  )
  )
  );
});

export default PostDetailBottomMenu;
